import { clipEndBar } from '@/audio/clipTiming';
import coachCopy from '@/data/classCoach.json';
import songMaps from '@/data/classSongMaps.json';
import { SECTION_COLORS } from '@/state/autoArranger';

const LEGACY_BASE_MAP = [
  ['Intro', 4], ['Hook', 8], ['Verse', 16], ['Hook', 8], ['Outro', 4],
];
const EPS = 1e-6;

export const classFlow = (classSession) => (classSession?.flow === 2 ? 2 : 1);
export const classStepsForFlow = (flow) => (flow === 2 ? coachCopy.steps : coachCopy.stepsV1);
export const classMapCanRefresh = (classSession) => classSession?.flow === 2
  && !(Array.isArray(classSession.progress?.done) && classSession.progress.done.includes('fly'));

export function selectClassSongMapId(bpm) {
  const tempo = Number(bpm);
  const scored = songMaps.templates.map((template) => {
    const seconds = template.length * 240 / tempo;
    return { template, seconds, distance: Math.abs(seconds - 105) };
  });
  const inRange = scored.filter(({ seconds }) => seconds >= 90 && seconds <= 120);
  return (inRange.length ? inRange : scored)
    .sort((a, b) => a.distance - b.distance || a.template.length - b.template.length)[0].template.id;
}

function materializeMap(template) {
  let startBar = 0;
  const sections = template.sections.map(([name, bars]) => {
    const section = { name, startBar, bars, color: SECTION_COLORS[name] };
    startBar += bars;
    return section;
  });
  return { id: template.id, sections, length: template.length };
}

export function classSongMap(bpm, flow = 1, mapId = null) {
  if (flow === 2) {
    const selectedId = songMaps.templates.some((template) => template.id === mapId)
      ? mapId : selectClassSongMapId(bpm);
    return materializeMap(songMaps.templates.find((template) => template.id === selectedId));
  }
  const scale = Number(bpm) >= 120 ? 2 : 1;
  return materializeMap({
    id: 'legacy',
    sections: LEGACY_BASE_MAP.map(([name, bars]) => [name, bars * scale]),
    length: LEGACY_BASE_MAP.reduce((total, [, bars]) => total + bars * scale, 0),
  });
}

export function formatClassDuration(bars, bpm) {
  const seconds = Math.round(Number(bars) * 240 / Number(bpm));
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}

const normalizedName = (value) => String(value || '').trim().toLowerCase();
const hasMarker = (markers, name) => markers.some((marker) => normalizedName(marker.name) === normalizedName(name));
const audibleTracks = (tracks) => tracks.filter((track) => !track.muted && !track.inBank);
const eligibleTracks = (tracks) => tracks.filter((track) => !track.inBank);
const overlaps = (clip, start, end, bpm) => Number(clip.start_bar || 0) < end - EPS && clipEndBar(clip, bpm) > start + EPS;
const trackOverlaps = (track, start, end, bpm) => (track.clips || []).some((clip) => overlaps(clip, start, end, bpm));

export function songEndBar(tracks = [], bpm) {
  return tracks.reduce((latest, track) => (track.clips || []).reduce(
    (end, clip) => Math.max(end, clipEndBar(clip, bpm)), latest,
  ), 0);
}

function coveredBars(track, start, end, bpm) {
  const intervals = (track.clips || []).map((clip) => [
    Math.max(start, Number(clip.start_bar || 0)), Math.min(end, clipEndBar(clip, bpm)),
  ]).filter(([left, right]) => right > left + EPS).sort((a, b) => a[0] - b[0]);
  let covered = 0;
  let cursorStart = null;
  let cursorEnd = null;
  intervals.forEach(([left, right]) => {
    if (cursorStart === null) { cursorStart = left; cursorEnd = right; return; }
    if (left <= cursorEnd + EPS) cursorEnd = Math.max(cursorEnd, right);
    else { covered += cursorEnd - cursorStart; cursorStart = left; cursorEnd = right; }
  });
  return covered + (cursorStart === null ? 0 : cursorEnd - cursorStart);
}

function markersComplete(markers, sections) {
  return sections.every((section) => markers.some((marker) => normalizedName(marker.name) === normalizedName(section.name)
    && Math.abs(Number(marker.startBar) - section.startBar) <= 1));
}

function flow2RuleComplete(stepId, { tracks, sectionMarkers, bpm, classSession, mapId, progress }) {
  const map = classSongMap(bpm, 2, classSession?.mapId || mapId);
  const eligible = eligibleTracks(tracks);
  const first = (name) => map.sections.find((section) => section.name === name);
  const intro = first('Intro');
  const hook = first('Hook');
  const verse = first('Verse');
  const outro = first('Outro');
  const savedProgress = classSession?.progress || progress || {};

  if (stepId === 'fly') return eligible.length >= 2 && eligible.every((track) => songEndBar([track], bpm) >= map.length - 0.25);
  if (stepId === 'markers') return markersComplete(sectionMarkers, map.sections);
  if (stepId === 'intro') {
    const drums = eligible.filter((track) => track.instrument === 'drums');
    if (drums.length) {
      return drums.every((track) => !trackOverlaps(track, intro.startBar, intro.startBar + intro.bars, bpm))
        && eligible.some((track) => track.instrument !== 'drums' && trackOverlaps(track, intro.startBar, intro.startBar + intro.bars, bpm));
    }
    return eligible.some((track) => trackOverlaps(track, intro.startBar, intro.startBar + intro.bars, bpm))
      && eligible.some((track) => !trackOverlaps(track, intro.startBar, intro.startBar + intro.bars, bpm));
  }
  if (stepId === 'hook') return savedProgress.heardHook === true;
  if (stepId === 'verse') return eligible.some((track) => trackOverlaps(track, hook.startBar, hook.startBar + hook.bars, bpm)
    && coveredBars(track, verse.startBar, verse.startBar + verse.bars, bpm) <= verse.bars * 0.25 + EPS);
  if (stepId === 'flavor') return eligible.some((track) => (track.clips || []).some(
    (clip) => Number(clip.speed ?? 1) !== 1 || Number(clip.pitch || 0) !== 0,
  ));
  if (stepId === 'outro') {
    const hookCount = eligible.filter((track) => trackOverlaps(track, hook.startBar, hook.startBar + hook.bars, bpm)).length;
    const outroCount = eligible.filter((track) => trackOverlaps(track, outro.startBar, outro.startBar + outro.bars, bpm)).length;
    const end = songEndBar(eligible, bpm);
    return outroCount < hookCount && end >= map.length - 0.25 && end <= map.length + 0.25;
  }
  if (stepId === 'listen') return savedProgress.listenedThrough === true;
  return false;
}

function legacyComplete(stepId, { tracks, sectionMarkers, bpm }) {
  const audible = audibleTracks(tracks);
  const picked = eligibleTracks(tracks);
  if (stepId === 'seed') return songEndBar(picked, bpm) >= 16;
  if (stepId === 'intro') {
    if (hasMarker(sectionMarkers, 'Intro')) return true;
    const drumsInIntro = audible.some((track) => track.instrument === 'drums' && trackOverlaps(track, 0, 4, bpm));
    const otherInIntro = audible.some((track) => track.instrument !== 'drums' && trackOverlaps(track, 0, 4, bpm));
    return !drumsInIntro && otherInIntro;
  }
  if (stepId === 'hook') return hasMarker(sectionMarkers, 'Hook');
  if (stepId === 'verse') return hasMarker(sectionMarkers, 'Verse');
  if (stepId === 'flavor') return picked.some((track) => (track.clips || []).some(
    (clip) => Number(clip.speed ?? 1) !== 1 || Number(clip.pitch || 0) !== 0,
  ));
  if (stepId === 'outro') return hasMarker(sectionMarkers, 'Outro')
    && songEndBar(picked, bpm) >= classSongMap(bpm, 1).length - 4;
  return false;
}

export function classStepComplete(stepId, options = {}) {
  const normalized = {
    tracks: options.tracks || [], sectionMarkers: options.sectionMarkers || [], bpm: options.bpm || 90,
    classSession: options.classSession, mapId: options.mapId, progress: options.progress,
  };
  const flow = options.flow === 2 || options.classSession?.flow === 2 ? 2 : 1;
  if (flow !== 2) return legacyComplete(stepId, normalized);
  const done = normalized.classSession?.progress?.done || normalized.progress?.done || [];
  return (Array.isArray(done) && done.includes(stepId)) || flow2RuleComplete(stepId, normalized);
}

export function recordClassProgressDone(classSession, fromStep, toStep) {
  if (classSession?.flow !== 2 || toStep <= fromStep) return classSession;
  const steps = classStepsForFlow(2);
  const progress = classSession.progress || {};
  const skipped = new Set(Array.isArray(progress.skipped) ? progress.skipped : []);
  const done = new Set(Array.isArray(progress.done) ? progress.done : []);
  steps.slice(fromStep, toStep).forEach((step) => {
    if (!skipped.has(step.id)) done.add(step.id);
  });
  return { ...classSession, progress: { ...progress, done: [...done] } };
}

export function reachedClassSteps(arrangement = {}) {
  const flow = classFlow(arrangement.classSession);
  const steps = classStepsForFlow(flow);
  const skipped = new Set(flow === 2 && Array.isArray(arrangement.classSession?.progress?.skipped)
    ? arrangement.classSession.progress.skipped : []);
  const reached = [];
  for (const step of steps) {
    const complete = classStepComplete(step.id, { ...arrangement, flow });
    if (!complete && !skipped.has(step.id)) break;
    if (complete) reached.push(step.id);
  }
  return reached;
}

export function addClassMapMarkers(sectionMarkers = [], bpm, idFactory = () => crypto.randomUUID(), flow = 1, mapId = null) {
  const next = [...sectionMarkers];
  classSongMap(bpm, flow, mapId).sections.forEach((section) => {
    const duplicate = next.some((marker) => normalizedName(marker.name) === normalizedName(section.name)
      && Number(marker.startBar) === section.startBar);
    if (!duplicate) next.push({ id: idFactory(), name: section.name, startBar: section.startBar, color: section.color });
  });
  return next;
}

export const classSteps = coachCopy.steps;
export const classStepsV1 = coachCopy.stepsV1;
